import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import { UserProfileId } from "@sp-crm/core";
import { HoveredMapEntity } from "components/community-search/community-map/types";
import { SearchUserProfilesQuery } from "generated/graphql";
import React, { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { AgentDirectorySearchCard } from "./agent-directory-search-card";

interface AgentDirectorySearchListProps {
    agents: SearchUserProfilesQuery["searchUserProfiles"]["hits"];
    onHover: (agent: HoveredMapEntity | null) => void;
    referenceContacts: SearchUserProfilesQuery["searchUserProfiles"]["referenceContacts"];
    addReferenceContactFromAgentDirectory: (userProfileId: UserProfileId) => void;
}

export const AgentDirectorySearchList: React.FC<
    AgentDirectorySearchListProps
> = props => {
    const {
        agents: agentHits,
        onHover,
        referenceContacts,
        addReferenceContactFromAgentDirectory,
    } = props;

    const handleHover = useCallback(
        (agent: HoveredMapEntity) => {
            onHover(agent);
        },
        [onHover],
    );

    const handleEndHover = useCallback(() => {
        onHover(null);
    }, [onHover]);

    return (
        <ul className="space-y-4">
            {agentHits.map(agent => {
                const referenceContact = referenceContacts.find(
                    rc => rc.userProfileId === agent.id,
                );
                return (
                    <li key={agent.id}>
                        <AgentDirectorySearchCard
                            agent={agent}
                            onHover={handleHover}
                            onEndHover={handleEndHover}
                            detailsMode={"expanded"}>
                            {referenceContact ? (
                                <Link
                                    to={referenceContact.appLink}
                                    className="relative inline-flex w-0 flex-1 items-center justify-center rounded-bl-lg border border-transparent py-4 text-sm font-medium text-brand-600 bg-brand-100 hover:bg-gray-100 hover:text-gray-800 twoverride">
                                    <CheckCircleIcon className="w-4 h-4" />
                                    <span className="ml-2">View contact</span>
                                </Link>
                            ) : (
                                <a
                                    onClick={e => {
                                        e.preventDefault();
                                        addReferenceContactFromAgentDirectory(agent.id);
                                    }}
                                    className="relative inline-flex w-0 flex-1 items-center justify-center rounded-bl-lg border border-transparent py-4 text-sm font-medium text-brand-600 hover:bg-brand-100 hover:text-brand-800 twoverride">
                                    <span className="ml-3">Save Contact</span>
                                </a>
                            )}
                        </AgentDirectorySearchCard>
                    </li>
                );
            })}
        </ul>
    );
};

interface AgentDirectoryPickerListProps {
    agents: SearchUserProfilesQuery["searchUserProfiles"]["hits"];
    value: UserProfileId | null;
    onChange: (value: UserProfileId | null) => void;
}

export const AgentDirectoryPickerList: React.FC<
    AgentDirectoryPickerListProps
> = props => {
    const { agents, value, onChange } = props;

    const selectedAgent = useMemo(() => {
        return agents.find(agent => agent.id === value);
    }, [agents, value]);

    if (selectedAgent) {
        return (
            <div>
                <a
                    href="#"
                    className="flex items-center space-x-2"
                    onClick={e => {
                        e.preventDefault();
                        onChange(null);
                    }}>
                    <ArrowLeftIcon className="w-4 h-4" />
                    <div>Select another advisor</div>
                </a>
                <AgentDirectorySearchCard
                    agent={selectedAgent}
                    onHover={() => {}}
                    onEndHover={() => {}}
                    detailsMode={"collapsed"}>
                    <a
                        onClick={e => {
                            e.preventDefault();
                        }}
                        className="relative inline-flex w-0 flex-1 items-center justify-center rounded-br-lg border border-transparent py-4 text-sm font-medium bg-green-100 text-green-600 hover:bg-gray-100 hover:text-gray-800 twoverride group">
                        <CheckCircleIcon className="w-4 h-4 text-green-800 group-hover:text-grey-800" />
                        <span className="ml-2">Selected Advisor</span>
                    </a>
                </AgentDirectorySearchCard>
            </div>
        );
    }

    return (
        <ul className="space-y-4">
            {agents.map(agent => {
                return (
                    <li key={agent.id}>
                        <AgentDirectorySearchCard
                            agent={agent}
                            onHover={() => {}}
                            onEndHover={() => {}}
                            detailsMode={"collapsed"}>
                            <a
                                onClick={e => {
                                    e.preventDefault();
                                    onChange(agent.id);
                                }}
                                className="relative inline-flex w-0 flex-1 items-center justify-center rounded-bl-lg border border-transparent py-4 text-sm font-medium text-brand-600 bg-brand-100 hover:bg-gray-100 hover:text-gray-800 twoverride">
                                <span className="ml-3">Select advisor</span>
                            </a>
                        </AgentDirectorySearchCard>
                    </li>
                );
            })}
        </ul>
    );
};
